import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RecipeIndexEntry } from '../services/unifiedRecipeService';
import { logout } from './authSlice';

interface RecipeIndexState {
  entries: RecipeIndexEntry[];
  lastSyncedAt: string | null;
  userId: string | null;
}

const initialState: RecipeIndexState = {
  entries: [],
  lastSyncedAt: null,
  userId: null,
};

const recipeIndexSlice = createSlice({
  name: 'recipeIndex',
  initialState,
  reducers: {
    setRecipeIndex: (
      state,
      action: PayloadAction<{ entries: RecipeIndexEntry[]; userId: string | null }>
    ) => {
      state.entries = action.payload.entries;
      state.lastSyncedAt = new Date().toISOString();
      state.userId = action.payload.userId;
    },
    // Marks the index stale so the next screen focus triggers a background refresh,
    // but keeps entries so Phase 1 can still render stale data instantly.
    invalidateRecipeIndex: (state) => {
      state.lastSyncedAt = null;
    },
    // Patches a single entry in place after an inline fix (classification,
    // edit) so the persisted cache is correct immediately — without this, a
    // page refresh within the TTL re-renders the pre-fix entry.
    patchRecipeIndexEntry: (
      state,
      action: PayloadAction<{ id: string; changes: Partial<RecipeIndexEntry> }>
    ) => {
      const entry = state.entries.find(e => e.id === action.payload.id);
      if (entry) Object.assign(entry, action.payload.changes);
    },
    // Drops a deleted recipe from the persisted cache. The Recipes screen draws
    // this cache on focus for up to 30 minutes, so without it the recipe the
    // cook just deleted was still in the list they were sent back to (#793).
    removeRecipeIndexEntry: (state, action: PayloadAction<string>) => {
      state.entries = state.entries.filter(e => e.id !== action.payload);
    },
    clearRecipeIndex: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(logout.fulfilled, () => initialState);
  },
});

export const {
  setRecipeIndex,
  invalidateRecipeIndex,
  patchRecipeIndexEntry,
  removeRecipeIndexEntry,
  clearRecipeIndex,
} = recipeIndexSlice.actions;

export const selectRecipeIndex = (state: { recipeIndex: RecipeIndexState }) =>
  state.recipeIndex.entries;

/**
 * Returns true when the persisted index is absent, belongs to a different user,
 * or is older than the given TTL. Default TTL is 30 minutes.
 */
export const isRecipeIndexStale = (
  state: { recipeIndex: RecipeIndexState },
  currentUserId: string | null,
  ttlMs = 30 * 60 * 1000
): boolean => {
  const { lastSyncedAt, userId } = state.recipeIndex;
  if (lastSyncedAt === null) return true;
  if (userId !== currentUserId) return true;
  return Date.now() - new Date(lastSyncedAt).getTime() > ttlMs;
};

export default recipeIndexSlice.reducer;
